/**
 * useSimTraces.ts · Fetches the LATEST daganzo + LATEST mpc-lambda traces
 *
 * Two-hop fetch:
 *   1. GET /api/sim_runs — list summaries (backend orders by id desc).
 *   2. Pick the latest run for `daganzo` and `mpc-lambda` controllers.
 *   3. GET /api/sim_runs/{id}/trace for both, in parallel.
 *
 * If either controller has no runs, we surface that as `error` without
 * blocking the other. The hook is stable across renders — `loading` only
 * flips to `false` once BOTH fetches settle (success or failure).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { ApiError, getSimRuns, getSimTrace, isAbortError, } from "../lib/api";
function pickLatest(runs, controller) {
    // Backend returns most-recent-first; still guard by explicit `id` sort.
    const filtered = runs.filter((r) => r.controller === controller);
    if (filtered.length === 0)
        return null;
    return filtered.reduce((a, b) => (a.id > b.id ? a : b));
}
export function useSimTraces(options = {}) {
    const { refetchIntervalMs = 15_000, enabled = true } = options;
    const [daganzo, setDaganzo] = useState(null);
    const [mpc, setMpc] = useState(null);
    const [daganzoRun, setDaganzoRun] = useState(null);
    const [mpcRun, setMpcRun] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const abortRef = useRef(null);
    const timerRef = useRef(null);
    const mountedRef = useRef(true);
    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            abortRef.current?.abort();
            if (timerRef.current !== null) {
                window.clearInterval(timerRef.current);
                timerRef.current = null;
            }
        };
    }, []);
    useEffect(() => {
        if (!enabled)
            return;
        const fetchOnce = async () => {
            abortRef.current?.abort();
            const ac = new AbortController();
            abortRef.current = ac;
            try {
                const runs = await getSimRuns(50, { signal: ac.signal });
                if (!mountedRef.current)
                    return;
                const dagRun = pickLatest(runs, "daganzo");
                const mpcRun_ = pickLatest(runs, "mpc-lambda");
                setDaganzoRun(dagRun);
                setMpcRun(mpcRun_);
                if (!dagRun && !mpcRun_) {
                    setError("No sim runs available yet — trigger the sim engine first.");
                    setLoading(false);
                    return;
                }
                const [dagTrace, mpcTrace] = await Promise.allSettled([
                    dagRun
                        ? getSimTrace(dagRun.id, { signal: ac.signal })
                        : Promise.resolve(null),
                    mpcRun_
                        ? getSimTrace(mpcRun_.id, { signal: ac.signal })
                        : Promise.resolve(null),
                ]);
                if (!mountedRef.current)
                    return;
                const errors = [];
                if (dagTrace.status === "fulfilled") {
                    setDaganzo(dagTrace.value);
                }
                else if (!isAbortError(dagTrace.reason)) {
                    errors.push(describeError("daganzo", dagTrace.reason));
                }
                if (mpcTrace.status === "fulfilled") {
                    setMpc(mpcTrace.value);
                }
                else if (!isAbortError(mpcTrace.reason)) {
                    errors.push(describeError("mpc-lambda", mpcTrace.reason));
                }
                setError(errors.length > 0 ? errors.join(" · ") : null);
                setLoading(false);
            }
            catch (err) {
                if (isAbortError(err) || !mountedRef.current)
                    return;
                setError(describeError("sim_runs", err));
                setLoading(false);
            }
        };
        void fetchOnce();
        if (refetchIntervalMs > 0) {
            timerRef.current = window.setInterval(() => {
                void fetchOnce();
            }, refetchIntervalMs);
        }
        return () => {
            if (timerRef.current !== null) {
                window.clearInterval(timerRef.current);
                timerRef.current = null;
            }
        };
    }, [enabled, refetchIntervalMs]);
    return useMemo(() => ({ daganzo, mpc, daganzoRun, mpcRun, loading, error }), [daganzo, mpc, daganzoRun, mpcRun, loading, error]);
}
function describeError(scope, err) {
    if (err instanceof ApiError) {
        return `${scope} · ${err.short}`;
    }
    if (err instanceof Error) {
        return `${scope} · ${err.message}`;
    }
    return `${scope} · ${String(err)}`;
}
